import { CommonModule } from '@angular/common';
import {
  Component, ElementRef, EventEmitter, Input,
  NgZone, OnChanges, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { DirectusService } from '../core/services/directus.service';

// ── Types ─────────────────────────────────────────────────────────────────────

// Keys must match exactly what the website frontend uses in croppedAssetUrl()
export type CropMode = 'card' | 'hero_desktop' | 'hero_mobile' | 'avatar';

export interface CropConfig {
  label: string;
  ar: number;      // width / height
  prevW: number;   // preview thumbnail width (px)
  prevH: number;   // preview thumbnail height (px)
  circle?: boolean;
}

export const CROP_CONFIGS: Record<CropMode, CropConfig> = {
  card:         { label: 'Card',   ar: 280 / 350, prevW: 54,  prevH: 67  },
  hero_desktop: { label: 'Home',   ar: 410 / 550, prevW: 50,  prevH: 67  },
  hero_mobile:  { label: 'Mobile', ar: 375 / 256, prevW: 80,  prevH: 55  },
  avatar:       { label: 'Avatar', ar: 1,         prevW: 60,  prevH: 60, circle: true }
};

export interface CropData { left: number; top: number; width: number; height: number; }
export type CropsRecord = Partial<Record<CropMode, CropData>>;
export interface ImageCropResult { file: File | null; crops: CropsRecord; }

// ── Internal ──────────────────────────────────────────────────────────────────

interface Box { x: number; y: number; w: number; h: number; }
type Corner = 'tl' | 'tr' | 'bl' | 'br';
type DS =
  | { kind: 'move'; mx0: number; my0: number; bx0: number; by0: number }
  | { kind: 'resize'; corner: Corner; fx: number; fy: number };

const MIN_PX = 40;

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  standalone: true,
  selector: 'app-image-crop-upload',
  imports: [CommonModule],
  template: `
    <div class="icu-root">

      <!-- Drop zone -->
      <div class="icu-drop" *ngIf="!src"
           (click)="pick()"
           (dragover)="$event.preventDefault()"
           (drop)="onDrop($event)">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
        </svg>
        <p>Click to upload or drag &amp; drop</p>
        <span>JPG, PNG, WEBP</span>
      </div>

      <!-- Editor -->
      <div class="icu-editor" *ngIf="src">

        <!-- Canvas column -->
        <div class="icu-col-canvas">
          <div class="icu-wrap" #wrapEl
               [style.cursor]="cursor"
               (mousedown)="onDown($event)"
               (mousemove)="onMove($event)"
               (mouseup)="onUp()"
               (mouseleave)="onUp()">

            <img #imgEl [src]="src" class="icu-img" (load)="onImgLoad()" draggable="false" />

            <ng-container *ngIf="ready">
              <!-- Dark masks around active crop -->
              <div class="icu-mask" [style.height.px]="crop.y" style="top:0;left:0;right:0"></div>
              <div class="icu-mask" [style.top.px]="crop.y + crop.h" style="left:0;right:0;bottom:0"></div>
              <div class="icu-mask" [style.top.px]="crop.y" [style.width.px]="crop.x" [style.height.px]="crop.h" style="left:0"></div>
              <div class="icu-mask" [style.top.px]="crop.y" [style.left.px]="crop.x + crop.w" [style.height.px]="crop.h" style="right:0"></div>

              <!-- Inactive crop outlines -->
              <ng-container *ngFor="let im of inactiveModes">
                <div class="icu-inactive"
                     [style.left.px]="getBox(im).x"  [style.top.px]="getBox(im).y"
                     [style.width.px]="getBox(im).w"  [style.height.px]="getBox(im).h">
                  <span class="icu-inactive-lbl">{{ cfg(im).label }}</span>
                </div>
              </ng-container>

              <!-- Active crop box -->
              <div class="icu-box"
                   [style.left.px]="crop.x" [style.top.px]="crop.y"
                   [style.width.px]="crop.w" [style.height.px]="crop.h">
                <div class="icu-grid-h" style="top:33.33%"></div>
                <div class="icu-grid-h" style="top:66.66%"></div>
                <div class="icu-grid-v" style="left:33.33%"></div>
                <div class="icu-grid-v" style="left:66.66%"></div>
                <span class="icu-corner tl" (mousedown)="onCornerDown($event,'tl')"></span>
                <span class="icu-corner tr" (mousedown)="onCornerDown($event,'tr')"></span>
                <span class="icu-corner bl" (mousedown)="onCornerDown($event,'bl')"></span>
                <span class="icu-corner br" (mousedown)="onCornerDown($event,'br')"></span>
              </div>
            </ng-container>
          </div>

          <!-- Mode tabs -->
          <div class="icu-tabs">
            <button *ngFor="let m of modes" class="icu-tab"
                    [class.active]="mode === m"
                    (click)="setMode(m)">
              {{ cfg(m).label }}
            </button>
          </div>
          <button class="icu-change" (click)="pick()">Change photo</button>
        </div>

        <!-- Previews column -->
        <div class="icu-col-prev" *ngIf="ready">
          <ng-container *ngFor="let m of modes">
            <div class="icu-prev-group">
              <div class="icu-prev"
                   [class.icu-prev-circle]="cfg(m).circle"
                   [style.width.px]="cfg(m).prevW"
                   [style.height.px]="cfg(m).prevH"
                   [ngStyle]="previewStyle(m)">
              </div>
              <span class="icu-prev-lbl">{{ cfg(m).label }}</span>
            </div>
          </ng-container>
        </div>
      </div>

      <input #fileInput type="file" accept="image/*" (change)="onFile($event)" style="display:none" />
    </div>
  `,
  styles: [`
    :host { display: block; }
    .icu-root { width: 100%; }

    /* Drop zone */
    .icu-drop {
      border: 2px dashed var(--app-border);
      border-radius: var(--app-radius);
      padding: 2.5rem 1.5rem;
      text-align: center;
      cursor: pointer;
      color: var(--app-muted);
      transition: border-color 0.15s, background 0.15s;
    }
    .icu-drop:hover {
      border-color: var(--jci-blue);
      background: rgba(0,151,215,0.04);
      color: var(--jci-blue);
    }
    .icu-drop svg { margin-bottom: 0.5rem; opacity: 0.55; }
    .icu-drop p   { margin: 0 0 0.2rem; font-size: 0.875rem; font-weight: 600; }
    .icu-drop span { font-size: 0.75rem; opacity: 0.65; }

    /* Editor layout */
    .icu-editor { display: flex; gap: 1.25rem; align-items: flex-start; }
    .icu-col-canvas { flex: 1; min-width: 0; }
    .icu-col-prev {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding-top: 0.25rem;
      flex-shrink: 0;
    }

    /* Canvas */
    .icu-wrap {
      position: relative;
      display: block;
      overflow: hidden;
      border-radius: var(--app-radius);
      line-height: 0;
      background: #111;
      user-select: none;
    }
    .icu-img { display: block; width: 100%; height: auto; opacity: 0.9; }

    /* Masks */
    .icu-mask {
      position: absolute;
      background: rgba(0,0,0,0.55);
      pointer-events: none;
    }

    /* Inactive outlines */
    .icu-inactive {
      position: absolute;
      border: 1.5px dashed rgba(255,255,255,0.4);
      box-sizing: border-box;
      pointer-events: none;
    }
    .icu-inactive-lbl {
      position: absolute;
      top: 3px; left: 4px;
      font-size: 0.6rem;
      color: rgba(255,255,255,0.6);
      background: rgba(0,0,0,0.35);
      padding: 1px 4px;
      border-radius: 3px;
      line-height: 1.4;
    }

    /* Active crop box */
    .icu-box {
      position: absolute;
      border: 2px solid #fff;
      box-sizing: border-box;
      pointer-events: none;
    }
    .icu-grid-h, .icu-grid-v {
      position: absolute;
      background: rgba(255,255,255,0.22);
    }
    .icu-grid-h { left: 0; right: 0; height: 1px; }
    .icu-grid-v { top: 0; bottom: 0; width: 1px; }

    /* Corner handles */
    .icu-corner {
      position: absolute;
      width: 12px; height: 12px;
      background: #fff;
      border-radius: 2px;
      pointer-events: all;
      box-shadow: 0 1px 3px rgba(0,0,0,0.4);
    }
    .icu-corner.tl { top: -1px; left: -1px; cursor: nwse-resize; }
    .icu-corner.tr { top: -1px; right: -1px; cursor: nesw-resize; }
    .icu-corner.bl { bottom: -1px; left: -1px; cursor: nesw-resize; }
    .icu-corner.br { bottom: -1px; right: -1px; cursor: nwse-resize; }

    /* Mode tabs */
    .icu-tabs { display: flex; gap: 0.375rem; margin-top: 0.625rem; flex-wrap: wrap; }
    .icu-tab {
      flex: 1;
      min-width: 56px;
      padding: 0.3rem 0.5rem;
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      background: transparent;
      font-size: 0.72rem;
      cursor: pointer;
      color: var(--app-muted);
      font-family: inherit;
      transition: all 0.15s;
      white-space: nowrap;
    }
    .icu-tab.active {
      background: var(--jci-blue);
      border-color: var(--jci-blue);
      color: #fff;
      font-weight: 600;
    }
    .icu-change {
      width: 100%;
      margin-top: 0.375rem;
      padding: 0.35rem;
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      background: transparent;
      font-size: 0.75rem;
      cursor: pointer;
      color: var(--app-muted);
      font-family: inherit;
      transition: all 0.15s;
    }
    .icu-change:hover { border-color: var(--jci-blue); color: var(--jci-blue); }

    /* Previews */
    .icu-prev-group { display: flex; flex-direction: column; align-items: center; gap: 0.3rem; }
    .icu-prev {
      background-repeat: no-repeat;
      background-color: var(--app-surface-soft);
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      flex-shrink: 0;
      overflow: hidden;
    }
    .icu-prev-circle { border-radius: 50%; }
    .icu-prev-lbl { font-size: 0.68rem; color: var(--app-muted); }
  `]
})
export class ImageCropUploadComponent implements OnChanges {
  @Input() imageId: string | null = null;
  @Input() existingCrops: CropsRecord | null = null;
  @Input() modes: CropMode[] = ['avatar'];
  @Output() change = new EventEmitter<ImageCropResult>();

  @ViewChild('fileInput') fileInputEl!: ElementRef<HTMLInputElement>;
  @ViewChild('imgEl')     imgEl!: ElementRef<HTMLImageElement>;
  @ViewChild('wrapEl')    wrapEl!: ElementRef<HTMLDivElement>;

  src: string | null = null;
  selectedFile: File | null = null;
  ready = false;
  mode: CropMode = 'avatar';
  cursor = 'default';

  displayW = 0;
  displayH = 0;
  naturalW = 1;

  boxes: Partial<Record<CropMode, Box>> = {};

  private ds: DS | null = null;

  get crop(): Box    { return this.boxes[this.mode] ?? { x: 0, y: 0, w: 0, h: 0 }; }
  set crop(b: Box)   { this.boxes[this.mode] = b; }

  get inactiveModes(): CropMode[] { return this.modes.filter(m => m !== this.mode); }

  cfg(m: CropMode): CropConfig { return CROP_CONFIGS[m]; }
  getBox(m: CropMode): Box     { return this.boxes[m] ?? { x: 0, y: 0, w: 0, h: 0 }; }

  constructor(
    private readonly directus: DirectusService,
    private readonly zone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['modes'] && this.modes.length) {
      this.mode = this.modes[0];
    }
    if (changes['imageId'] && this.imageId && !this.selectedFile) {
      this.src = this.directus.buildAssetUrl(this.imageId);
    }
  }

  pick() { this.fileInputEl.nativeElement.value = ''; this.fileInputEl.nativeElement.click(); }

  onDrop(e: DragEvent) {
    e.preventDefault();
    const f = e.dataTransfer?.files[0];
    if (f) this.loadFile(f);
  }

  onFile(e: Event) {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (f) this.loadFile(f);
  }

  private loadFile(f: File) {
    this.ready = false;
    this.selectedFile = f;
    const r = new FileReader();
    r.onload = ev => this.zone.run(() => { this.src = ev.target?.result as string; });
    r.readAsDataURL(f);
  }

  onImgLoad() {
    const img = this.imgEl.nativeElement;
    this.naturalW  = img.naturalWidth;
    this.displayW  = img.offsetWidth;
    this.displayH  = img.offsetHeight;
    const scale    = this.displayW / this.naturalW;
    const ec       = this.existingCrops;
    for (const m of this.modes) {
      const raw = ec?.[m];
      if (raw?.width) {
        this.boxes[m] = { x: raw.left * scale, y: raw.top * scale, w: raw.width * scale, h: raw.height * scale };
      } else {
        this.initBox(m);
      }
    }
    this.ready = true;
    this.emitChange();
  }

  private initBox(m: CropMode) {
    const ar = CROP_CONFIGS[m].ar;
    let w = this.displayW, h = w / ar;
    if (h > this.displayH) { h = this.displayH; w = h * ar; }
    this.boxes[m] = { x: (this.displayW - w) / 2, y: (this.displayH - h) / 2, w, h };
  }

  setMode(m: CropMode) { this.mode = m; }

  // ── Drag ──────────────────────────────────────────────────────────────────

  onDown(e: MouseEvent) {
    const r  = this.wrapEl.nativeElement.getBoundingClientRect();
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    const c  = this.crop;
    if (mx >= c.x && mx <= c.x + c.w && my >= c.y && my <= c.y + c.h) {
      this.ds = { kind: 'move', mx0: mx, my0: my, bx0: c.x, by0: c.y };
      this.cursor = 'grabbing';
      e.preventDefault();
    }
  }

  onCornerDown(e: MouseEvent, corner: Corner) {
    e.stopPropagation();
    const c  = this.crop;
    const fx = corner.includes('l') ? c.x + c.w : c.x;
    const fy = corner.includes('t') ? c.y + c.h : c.y;
    this.ds  = { kind: 'resize', corner, fx, fy };
    e.preventDefault();
  }

  onMove(e: MouseEvent) {
    if (!this.ds) { this.cursor = this.hoverCursor(e); return; }
    const r  = this.wrapEl.nativeElement.getBoundingClientRect();
    const mx = e.clientX - r.left, my = e.clientY - r.top;

    if (this.ds.kind === 'move') {
      const c  = this.crop;
      const nx = Math.max(0, Math.min(this.displayW - c.w, this.ds.bx0 + mx - this.ds.mx0));
      const ny = Math.max(0, Math.min(this.displayH - c.h, this.ds.by0 + my - this.ds.my0));
      this.crop = { ...c, x: nx, y: ny };
    } else {
      const { corner, fx, fy } = this.ds;
      const ar = CROP_CONFIGS[this.mode].ar;
      let w = Math.max(MIN_PX, Math.abs(mx - fx));
      let h = w / ar;
      if (corner.includes('l') && fx - w < 0)           { w = fx;                 h = w / ar; }
      if (corner.includes('r') && fx + w > this.displayW){ w = this.displayW - fx; h = w / ar; }
      if (corner.includes('t') && fy - h < 0)           { h = fy;                 w = h * ar; }
      if (corner.includes('b') && fy + h > this.displayH){ h = this.displayH - fy; w = h * ar; }
      this.crop = {
        x: corner.includes('l') ? fx - w : fx,
        y: corner.includes('t') ? fy - h : fy,
        w, h
      };
    }
    this.emitChange();
  }

  onUp() { this.ds = null; this.cursor = 'default'; }

  private hoverCursor(e: MouseEvent): string {
    const r = this.wrapEl?.nativeElement.getBoundingClientRect();
    if (!r) return 'default';
    const c = this.crop;
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    return (mx >= c.x && mx <= c.x + c.w && my >= c.y && my <= c.y + c.h) ? 'grab' : 'default';
  }

  // ── Preview ────────────────────────────────────────────────────────────────

  previewStyle(m: CropMode): Record<string, string> {
    if (!this.src || !this.ready) return {};
    const b = this.getBox(m);
    if (!b.w || !b.h) return {};
    const { prevW, prevH } = CROP_CONFIGS[m];
    const sx = prevW / b.w, sy = prevH / b.h;
    return {
      'background-image':    `url('${this.src}')`,
      'background-size':     `${this.displayW * sx}px ${this.displayH * sy}px`,
      'background-position': `${-b.x * sx}px ${-b.y * sy}px`
    };
  }

  // ── Emit ──────────────────────────────────────────────────────────────────

  private emitChange() {
    const s = this.naturalW / this.displayW;
    const td = (b: Box): CropData => ({
      left:   Math.round(b.x * s),
      top:    Math.round(b.y * s),
      width:  Math.round(b.w * s),
      height: Math.round(b.h * s)
    });
    const crops: CropsRecord = {};
    for (const m of this.modes) {
      const b = this.boxes[m];
      if (b) crops[m] = td(b);
    }
    this.change.emit({ file: this.selectedFile, crops });
  }
}
